import {
  RoomTypeAvailabilityDTO,
  RoomTypeDTO,
  RateDTO,
  BookerDTO,
  CardDetailsDTO,
  CreditCardDTO,
  ReservationItemRoomDTO,
  LocationDTO,
  RoomTypeUpgradeDTO,
  AccountLookupDTO,
  SearchAvailabilityPropertyResultDTO,
  ApiDropdown,
} from 'src/services/api/data-contracts';
import { IApiDropdownOption, ReservationItemService } from 'src/types/common';
import useDateHelperUtils from 'src/composables/dateHelperUtils';
import useUtils from 'src/composables/utils';
import useRateHelperUtils from 'src/composables/rateHelperUtils.ts';

const dateHelperUtils = useDateHelperUtils();
const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();

const currencies = [
  'GBP',
  'EUR',
  'USD',
  'AUD',
  'ZAR',
  'CAD',
  'MXN',
  'NGN',
  'DKK',
  'AED',
  'KES',
] as const;
export type Currency = (typeof currencies)[number];

export class UserDTO {
  emailAddress: string;
  firstName: string;
  lastName: string;
  telephoneNumber: string;
  receiveBrandOffers: boolean;
  canChoosePaymentMethod: boolean;
  creditCards: CreditCardDTO[];
}

export enum NavigationPage {
  None = 0,
  Search = 1,
  RoomsAndRates = 2,
  Checkout = 3,
}

export enum ProcessStep {
  None = 1,
  RoomTypeSelection = 2,
  RatePlanSelection = 4,
  UpgradeSelection = 8,
  Occupancy = 16,
  Extras = 32,
  BookingSummary = 64,
  PersonalInformation = 128,
  Account = 256,
  AddressDetails = 512,
  PaymentDetails = 1024,
}

export enum ReservationPaymentType {
  PayByCard = 0,
  PayByIndividualInvoice = 1,
  PayByVoucher = 2,
  PayByCompanyInvoice = 3,
  PayByPayStack = 4,
}

export interface IAlternativeStay {
  fromDate: string;
  toDate: string;
  fromDateDisplay: string;
  numberOfNights: number;
  numberOfNightsDisplay: string;
  totalCost: number;
}

export interface IDateNumber {
  year: number;
  month: number;
  day: number;
}

export interface IDateRange {
  from: IDateNumber;
  to: IDateNumber;
}

export interface IDateRangeValue {
  from: string;
  to: string;
}

export type IDateUpdateReason =
  | 'add-day'
  | 'remove-day'
  | 'add-range'
  | 'remove-range'
  | 'mask'
  | 'locale'
  | 'year'
  | 'month';

export type IDateUpdateValue = string | Array<unknown> | object | null;

export interface IDateUpdateDetails {
  day: number;
  month: number;
  year: number;
  from?: IDateNumber;
  to?: IDateNumber;
}

export interface IRoom {
  roomId: string;
  roomNumber: number;
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfInfants: number;
}

export interface IOccupancyRoomDTO {
  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;
}

export interface INewBookingState {
  propertyId: number;
  languageCode: string;
  fromDate: string;
  toDate: string;
  promoCode?: string;
  hopGiftVoucherCode?: string;
  ratePlanCode?: string;
  rooms: ISearchRoom[];
  location?: LocationDTO;
  reservation: Reservation;
  paymentType: ReservationPaymentType;
  reservationItems: ReservationItem[];
  isApp: boolean;
}

export interface ISearchRoom extends IRoom {
  isEditing: boolean;
}

export class Guest {
  constructor() {}
  firstName: string;
  lastName: string;
  get fullname(): string {
    return this.firstName + ' ' + this.lastName;
  }
  get isValid(): boolean {
    const isFirstValid =
      this.firstName != null &&
      this.firstName != undefined &&
      this.firstName != '';
    const isLastValid =
      this.lastName != null &&
      this.lastName != undefined &&
      this.lastName != '';
    return isFirstValid && isLastValid;
  }
}

export class Booker {
  constructor() {
    this.optInToReceiveUpdates = false;
  }
  firstName: string;
  lastName: string;
  telephoneNumber: string;
  emailAddress: string;
  street: string;
  postCode: string;
  country: IApiDropdownOption;
  optInToReceiveUpdates: boolean;
  get fullname(): string {
    return this.firstName + ' ' + this.lastName;
  }
  get arePersonalDetailsValid(): boolean {
    return (
      !!this.firstName &&
      !!this.lastName &&
      !!this.emailAddress &&
      !!this.telephoneNumber
    );
  }
  get areAddressDetailsValid(): boolean {
    return !!this.street && this.country != null && !!this.postCode;
  }

  get isValid(): boolean {
    return this.arePersonalDetailsValid && this.areAddressDetailsValid;
  }

  toDTO(): BookerDTO {
    return {
      FirstName: this.firstName,
      LastName: this.lastName,
      Email: this.emailAddress,
      Phone: this.telephoneNumber,
      Street: this.street,
      City: null,
      CountryId: this.country?.Id,
      StateOrCounty: null,
      OptInToReceiveUpdates: this.optInToReceiveUpdates,
      ZipCode: this.postCode,
    } as BookerDTO;
  }
}

export class CreditCard {
  constructor() {
    this.saveCardForFutureUse = false;
  }

  creditCardId?: number;

  creditCardName: string;
  creditCardNumber: string;
  cardHolderEmail: string;
  expiryMonth: number;
  expiryYear: number;
  cvvNumber: string;
  cardType: IApiDropdownOption;
  saveCardForFutureUse: boolean;

  get isValid(): boolean {
    return (
      !!this.creditCardName &&
      !!this.creditCardNumber &&
      !!this.cvvNumber &&
      this.expiryMonth != 0 &&
      this.expiryMonth != null &&
      this.expiryYear != 0 &&
      this.expiryYear != null &&
      this.cardType != null
    );
  }

  toDTO(): CardDetailsDTO {
    return {
      CreditCardId: this.creditCardId,
      SaveCreditCardToIndividual: this.saveCardForFutureUse,

      CardNumber: this.creditCardNumber,
      NameOnCard: this.creditCardName,
      EmailAddress: this.cardHolderEmail,
      CreditCardTypeId: this.cardType?.Id,
      ExpiryMonth: this.expiryMonth,
      ExpiryYear: this.expiryYear,
      SecurityCode: this.cvvNumber,
    } as CardDetailsDTO;
  }
}

export class Reservation {
  constructor() {
    this.booker = new Booker();
    this.creditCard = new CreditCard();
    this.agreeToTermsAndConditions = false;
    this.hasAcceptedCustomTermsAndConditions = false;
    this.hopGiftDiscountAmount = 0;
  }
  booker: Booker;
  password: string;

  creditCard: CreditCard;
  specialRequests: string;
  comment: string;

  hopGiftDiscountAmount: number;

  agreeToTermsAndConditions: boolean;

  hasAcceptedCustomTermsAndConditions: boolean;

  poNumber: string;
  company: AccountLookupDTO;

  get invoiceDetailsCaptured(): boolean {
    return this.poNumber != null && this.poNumber != '' && this.company != null;
  }
}

export interface RoomTypeIdQuantityChecker {
  (roomTypeId: number): number;
}

export interface IReservationItemUpgrade {
  roomType: RoomTypeUpgradeDTO;
  rate: RateDTO;
  totalUpgradeCost: number;
  totalCostDifference: number;
}

export class ReservationItem {
  constructor() {
    this.haveSeenServices = false;
    this.areServicesLoaded = false;
    this.hasSelectableServices = false;

    this.haveSeenUpgrades = false;

    this.availableUpgrades = [];
  }
  dateFrom: string;
  dateTo: string;

  roomId: string;
  roomNumber: number;
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfInfants: number;

  get totalNumberOfGuests(): number {
    return this.numberOfAdults + this.numberOfChildren + this.numberOfInfants;
  }

  roomConfiguration: ApiDropdown;

  leadGuest: Guest;

  roomHeader: string;
  get guestSummary(): string {
    return utils.getGuestSummaryText(
      this.numberOfAdults,
      this.numberOfChildren,
      this.numberOfInfants,
    );
  }

  ratePlanId: number;
  ratePlan: RateDTO;
  roomTypeId: number;
  roomType: RoomTypeDTO;

  roomTypeQuantityChecker: RoomTypeIdQuantityChecker;

  // True if they have seen the upgrades for this RI.
  haveSeenUpgrades: boolean;
  availableUpgrades: IReservationItemUpgrade[];

  clearUpgrades() {
    this.availableUpgrades.splice(0); // maintain reactivity.
  }

  // check if there are upgrades for this rate to any room type.
  checkForUpgrades() {
    const rpId = this.ratePlanId;

    if (this.roomTypeQuantityChecker == null) {
      this.roomTypeQuantityChecker = () => {
        return 0;
      };
    }

    if (this.roomType.Upgrades != null) {
      const ratePlanUpgrades = this.roomType.Upgrades.map((rtUpgrade) => {
        const availableQuantity = this.roomTypeQuantityChecker(
          rtUpgrade.RoomTypeId,
        );

        if (availableQuantity <= 0) {
          return null;
        }

        const matchingUpgradeRate = rtUpgrade.AvailableRates.find((ar) => {
          return ar.RatePlanId == rpId;
        });
        if (matchingUpgradeRate == null) {
          return null;
        } else {
          const utils = useUtils();

          const isRoomTypeOccupancyValid = utils.checkIfOccupancyIsValid(
            this.numberOfAdults,
            this.numberOfChildren,
            this.numberOfInfants,
            rtUpgrade.MaximumOccupancy,
            rtUpgrade.MinimumOccupancy,
            rtUpgrade.AdultMaximumOccupancy,
            rtUpgrade.ChildMaximumOccupancy,
          );

          let ratePlanOccupancyIsValid = true;
          if (matchingUpgradeRate.UseOccupancyForRatePlan) {
            ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
              this.numberOfAdults,
              this.numberOfChildren,
              this.numberOfInfants,
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.MaximumOccupancy,
                rtUpgrade.MaximumOccupancy,
              ),
              utils.getLargestMinimumValue(
                matchingUpgradeRate.MinimumOccupancy,
                rtUpgrade.MinimumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.AdultMaximumOccupancy,
                rtUpgrade.AdultMaximumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.ChildMaximumOccupancy,
                rtUpgrade.ChildMaximumOccupancy,
              ),
            );
          }

          if (isRoomTypeOccupancyValid && ratePlanOccupancyIsValid) {
            let cost = 0;
            const rateHelperUtils = useRateHelperUtils();
            cost = rateHelperUtils.calculateRoomRate(
              this.numberOfAdults,
              this.numberOfChildren,
              this.numberOfInfants,
              matchingUpgradeRate.BaseRate,
              matchingUpgradeRate.BaseNumberOfAdults,
              matchingUpgradeRate.CostPerRoom,
              matchingUpgradeRate.CostPerAdult,
              matchingUpgradeRate.CostPerChild,
              matchingUpgradeRate.CostPerInfant,
              matchingUpgradeRate.PerPersonCostPerAdult,
              matchingUpgradeRate.PerPersonCostPerChild,
              matchingUpgradeRate.PerPersonCostPerInfant,
            );

            return {
              roomType: rtUpgrade,
              rate: matchingUpgradeRate,
              totalUpgradeCost: cost,
              totalCostDifference: cost - this.totalCost,
            } as IReservationItemUpgrade;
          } else {
            return null;
          }
        }
      });
      this.availableUpgrades = ratePlanUpgrades.filter((ug) => {
        return ug != null;
      });

      if (!this.hasUpgrades) {
        this.haveSeenUpgrades = true;
      }
    }
  }

  get hasUpgrades(): boolean {
    const upgrades = this.availableUpgrades;
    return upgrades.length > 0;
  }

  promotion: IPromotion;

  // True if there are services available for selection
  hasSelectableServices: boolean;
  areServicesLoaded: boolean;
  haveSeenServices: boolean;
  services: Array<ReservationItemService> = [];

  getService(serviceId: number): ReservationItemService | null {
    const s = this.services.filter((u) => {
      return u.ServiceId == serviceId;
    });

    return s.length > 0 ? s[0] : null;
  }

  get totalServiceCost(): number {
    const totalDays = dateHelperUtils.calculateNumberOfNights(
      this.dateFrom,
      this.dateTo,
    );
    const totalGuests = this.numberOfAdults + this.numberOfChildren;
    const cost = rateHelperUtils.calcuateTotalServiceCost(
      this.services,
      totalDays,
      totalGuests,
    );
    return cost;
  }

  get totalRoomCost(): number {
    let cost = 0;
    cost = rateHelperUtils.calculateRoomRate(
      this.numberOfAdults,
      this.numberOfChildren,
      this.numberOfInfants,
      this.ratePlan.BaseRate,
      this.ratePlan.BaseNumberOfAdults,
      this.ratePlan.CostPerRoom,
      this.ratePlan.CostPerAdult,
      this.ratePlan.CostPerChild,
      this.ratePlan.CostPerInfant,
      this.ratePlan.PerPersonCostPerAdult,
      this.ratePlan.PerPersonCostPerChild,
      this.ratePlan.PerPersonCostPerInfant,
    );
    return cost;
  }

  get totalCost(): number {
    return this.totalRoomCost + this.totalServiceCost;
  }

  toDTO(): ReservationItemRoomDTO {
    return {
      DateFrom: this.dateFrom,
      DateTo: this.dateTo,
      RoomTypeId: this.roomType.RoomTypeId,
      RoomConfigurationDropdownOptionId: this.roomConfiguration?.Id,
      RatePlanId: this.ratePlan.RatePlanId,
      Promotion: this.ratePlan.Promotion,
      NumberOfAdults: this.numberOfAdults,
      NumberOfChildren: this.numberOfChildren,
      NumberOfInfants: this.numberOfInfants,
      LeadGuest: this.leadGuest,
      Services: this.services.map((rsi) => rsi.toReservationItemServiceDTO()),
      TotalCost: this.totalRoomCost, // Only room cost because we compare the rates to check.
    } as ReservationItemRoomDTO;
  }
}

export interface IPromotion {}

export class RoomTypeAvailability implements RoomTypeAvailabilityDTO {
  constructor(data: RoomTypeAvailabilityDTO) {
    Object.assign(this, data);

    this.LowestRate = -1;
    this.OriginalLowestRate = -1;
  }

  LowestRate: number;
  OriginalLowestRate: number;

  RoomType?: RoomTypeDTO;
  Rates?: RateDTO[];
  /** @format double */

  /** @format date-time */
  DateFrom?: string;
  /** @format date-time */
  DateTo?: string;
}

export class PropertySearchResultDTO {
  constructor() {
    this.propertyId = null;
    this.isSearching = false;
    this.result = null;
  }
  propertyId: number | null;
  isSearching: boolean;
  result: SearchAvailabilityPropertyResultDTO | null;
}
